import { useEffect, useMemo, useState } from 'react';

import { useAuth } from '@/hooks/useAuth';
import { createSupabaseClient } from '@/integrations/supabase/client';

export interface ProductItem {
  id: string;
  name: string;
  description: string;
  updatedAt: string;
}

type ProductRow = {
  id: string;
  name: string;
  description: string;
  updated_at: string;
};

type ProductShareRow = {
  products: ProductRow;
};

// eslint-disable-next-line max-lines-per-function
export const useProducts = () => {
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [loading, setLoading] = useState(true);
  const { user, getToken } = useAuth();

  const supabase = useMemo(() => createSupabaseClient(getToken), [getToken]);

  // eslint-disable-next-line max-lines-per-function
  useEffect(() => {
    // eslint-disable-next-line max-lines-per-function
    const fetchProducts = async () => {
      if (!user) {
        return;
      }

      setLoading(true);
      try {
        const [
          { data: ownedProducts, error: ownedError },
          { data: sharedProducts, error: sharedError },
        ] = await Promise.all([
          supabase
            .from('products')
            .select('id, name, description, updated_at')
            .eq('user_id', user.id)
            .order('updated_at', { ascending: false }),
          supabase
            .from('product_shares')
            .select('products!inner(id, name, description, updated_at)')
            .eq('user_id', user.id),
        ]);

        if (ownedError) {
          throw ownedError;
        }
        if (sharedError) {
          throw sharedError;
        }

        const ownedItems: ProductItem[] = (ownedProducts ?? []).map(
          (product: ProductRow) => ({
            id: product.id,
            name: product.name,
            description: product.description,
            updatedAt: product.updated_at,
          })
        );

        const ownedIds = new Set(ownedItems.map((product) => product.id));

        const sharedItems: ProductItem[] = (
          (sharedProducts as ProductShareRow[]) ?? []
        )
          .map((share) => {
            const product = share.products;
            return {
              id: product.id,
              name: product.name,
              description: product.description,
              updatedAt: product.updated_at,
            };
          })
          .filter((product) => !ownedIds.has(product.id));

        const allProducts = [...ownedItems, ...sharedItems].sort(
          (ownedProd, sharedProd) =>
            sharedProd.updatedAt.localeCompare(ownedProd.updatedAt)
        );

        setProducts(allProducts);
      } catch (error) {
        console.error('Error fetching recent products from user:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [user, supabase]);

  return {
    products,
    loading,
  };
};
